<template>
  <v-tabs
    v-bind="$attrs"
    :model-value="activeTab"
    :height="size === 'small' ? 48 : 64"
    :grow="kind !== 'fit'"
    :style="cssProps"
    :class="classes"
    :slider-size="$options.SLIDER_HEIGHT"
    :center-active="centerActive"
    :mandatory="mandatory"
    class="deck-tabs"
    prev-icon="fa-chevron-left fa-regular"
    next-icon="fa-chevron-right fa-regular"
    :show-arrows="!disableArrows"
    color="primary"
    @update:model-value="onTabChange"
  >
    <!-- @slot For `deck-tabs-tab` components. -->
    <slot />
  </v-tabs>
</template>

<script>
import { useToggle } from '@vueuse/core';

const SLIDER_HEIGHT = 2;

/**
 * A component that renders a tabbed navigation. It must be used in conjunction with `deck-tabs-tab` components as its children or grandchildren.
 */
export default {
  SLIDER_HEIGHT,
  name: 'DeckTabs',
  provide() {
    return {
      deckTabs: {
        register: this.register,
        unregister: this.unregister,
        size: this.size,
        kind: this.kind,
      },
    };
  },
  props: {
    /**
     * The index or named modelValue of the active tab.
     * @type {string | number}
     */
    modelValue: {
      type: [String, Number],
      default: '',
    },

    /**
     * The size of the tabs.
     * @type {'small' | 'large' | string}
     * @default 'small'
     */
    size: {
      type: String,
      default: 'small',
    },

    /**
     * The width behavior of the tabs.
     * @type {'grow' | 'fit' | 'equal' | string}
     * @default 'grow'
     */
    kind: {
      type: String,
      default: 'grow',
    },

    /**
     * Determines whether the active tab is centered when overflow is present.
     * @type {boolean}
     * @default false
     */
    centerActive: {
      type: Boolean,
      default: false,
    },

    /**
     * Determines whether the tabs are mandatory.
     * @type {boolean}
     * @default false
     */
    mandatory: {
      type: Boolean,
      default: false,
    },

    /**
     * Determines whether the tabs should not be scrollable with side arrows when overflow is present.
     * @type {boolean}
     * @default false
     */
    disableArrows: {
      type: Boolean,
      default: false,
    },

    /**
     * Determines whether the tabs should have a bottom border the same height of the slider.
     * @type {boolean}
     * @default false
     */
    withBottomBorder: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup() {
    const [isDestroyed, toggleDestroyed] = useToggle();

    onUnmounted(toggleDestroyed);

    return {
      isDestroyed,
    };
  },
  data() {
    return {
      activeTab: this.modelValue,
      tabs: [],
    };
  },
  computed: {
    cssProps() {
      return {
        '--deck-tabs-nav-size': this.size === 'small' ? '12px' : '16px',
        '--deck-tabs-slider-height': `${this.$options.SLIDER_HEIGHT}px`,
      };
    },

    classes() {
      return {
        'deck-tabs--disable-arrows': this.disableArrows,
        'deck-tabs--with-bottom-border': this.withBottomBorder,
      };
    },
  },
  watch: {
    modelValue(newValue, oldValue) {
      if (newValue === oldValue) return;
      this.activeTab = newValue;
    },
    activeTab(value) {
      /**
       * Reflects the tabs value. Bound to v-model.
       * @event update:modelValue
       */
      this.$emit('update:modelValue', value);
    },
  },
  methods: {
    /**
     * Registers a `deck-tabs-tab` component.
     * @param {VueComponent} tab
     */
    register(tab) {
      this.tabs.push(tab);
    },

    /**
     * Unregisters a `deck-tabs-tab` component.
     * @param {VueComponent} tab
     */
    unregister(tab) {
      if (this.isDestroyed) return;

      this.tabs.splice(this.tabs.indexOf(tab), 1);
    },

    async onTabChange(selectedTab) {
      this.activeTab = selectedTab;

      await nextTick();

      this.activeTab = this.modelValue;
    },
  },
};
</script>
<style lang="scss">
.deck-tabs {
  min-width: 0;

  &:where(.v-tabs) {
    background-color: transparent;
    flex: unset;
  }

  .v-tabs-bar {
    background-color: inherit !important;
  }

  :is(.v-slide-group__prev, .v-slide-group__next) {
    flex: 0 1 40px;
    min-width: 40px;

    &:is(:focus, :hover) .v-icon:not(.v-icon--disabled) {
      color: rgba(0, 0, 0, 0.87);
    }

    .v-icon {
      font-size: var(--deck-tabs-nav-size);

      &.v-icon--disabled {
        opacity: 0;
      }
    }
  }
}

.deck-tabs--disable-arrows {
  .v-slide-group__prev, .v-slide-group__next {
    display: none !important;
  }
}

.deck-tabs--with-bottom-border .v-slide-group__content {
  box-shadow: inset 0 -1px 0 0 var(--z-theme-background-secondary);
}
</style>
